import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { useAssignableRoles } from '../hooks/use-assignable-roles';
import { useAdminListStore } from '../store/admin-list.store';
import {
  ADMIN_STATUSES,
  ROLE_FILTER_ROOT,
  type AdminStatus,
} from '../types/admin.types';

// PRD doesn't spec a debounce window; 300 ms matches mock latency and avoids
// re-fetching on every keystroke once the real backend is wired.
const SEARCH_DEBOUNCE_MS = 300;

export function AdminListFilters() {
  const { t } = useTranslation('admin');
  const filters = useAdminListStore((s) => s.filters);
  const setFilters = useAdminListStore((s) => s.setFilters);

  // Reuse the Role Management query (per decision 4) so the dropdown stays
  // in sync with whatever Role Mgmt defines as the active role catalog.
  const roleOptions = useAssignableRoles();

  // Search-only debounce: local state drives the input for instant typing
  // feedback; the store (and therefore the query) only updates after the
  // trailing debounce. Role/status selects stay immediate — they are discrete
  // clicks, not high-frequency input.
  const [searchLocal, setSearchLocal] = useState(filters.search);
  const debouncedSearch = useDebouncedValue(searchLocal, SEARCH_DEBOUNCE_MS);

  // External resets (e.g. store.reset()) sync back to the local input.
  useEffect(() => {
    setSearchLocal(filters.search);
  }, [filters.search]);

  // Push the trailing debounced value to the store. Guarded so we don't fire
  // a redundant setFilters when the sync effect above just matched values.
  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      setFilters({ search: debouncedSearch });
    }
  }, [debouncedSearch]);

  return (
    <div className="filter-bar">
      <select
        className="filter-bar__select"
        style={{ width: 200 }}
        value={filters.role}
        onChange={(e) => setFilters({ role: e.target.value })}
      >
        <option value="">{t('adminManagement.filters.allRoles')}</option>
        <option value={ROLE_FILTER_ROOT}>{t('adminManagement.filters.rootRole')}</option>
        {roleOptions.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </select>

      <select
        className="filter-bar__select"
        style={{ width: 140 }}
        value={filters.status}
        onChange={(e) => setFilters({ status: e.target.value as AdminStatus | '' })}
      >
        <option value="">{t('adminManagement.filters.allStatuses')}</option>
        {ADMIN_STATUSES.map((s) => (
          <option key={s} value={s}>
            {t(`adminStatus.${s}`, { ns: 'labels' })}
          </option>
        ))}
      </select>

      <div className="filter-bar__search" style={{ flex: 1 }}>
        <input
          type="text"
          className="filter-bar__search-input"
          placeholder={t('adminManagement.filters.searchPlaceholder')}
          value={searchLocal}
          onChange={(e) => setSearchLocal(e.target.value)}
        />
        <Search className="filter-bar__search-icon" size={16} />
      </div>

      {/* Visible-but-disabled per decision 8 (locked to 20 items / page). */}
      <select className="filter-bar__select" style={{ marginLeft: 'auto' }} disabled value="20">
        <option value="20">{t('adminManagement.filters.itemsPerPage')}</option>
      </select>
    </div>
  );
}
